// Generic object pool. Avoids GC pressure from per-frame allocations
// (bullets, particles, decals, damage numbers, shell casings).
//
// Usage:
//   const bulletPool = new Pool(() => ({ pos: new Vector3(), vel: new Vector3(), ttl: 0, alive: false }),
//                               (b) => { b.alive = false; b.ttl = 0; });
//   const b = bulletPool.acquire();   b.alive = true;  ...
//   bulletPool.release(b);            // when done

export class Pool {
  /**
   * @param {() => T}   factory  Creates a fresh instance when the pool is empty.
   * @param {(item:T)=>void} [reset] Called on release to scrub state.
   * @param {number}    [initial=0] Pre-allocate this many instances.
   * @param {number}    [hardCap=Infinity] Refuse to grow past this.
   */
  constructor(factory, reset, initial = 0, hardCap = Infinity) {
    this._factory = factory;
    this._reset = reset || null;
    this._cap = hardCap;
    this._free = [];
    this._created = 0;
    for (let i = 0; i < initial; i++) {
      const item = factory();
      this._created++;
      if (this._reset) this._reset(item);
      this._free.push(item);
    }
  }

  /** Get an instance. May return null if pool is at hardCap and exhausted. */
  acquire() {
    if (this._free.length) return this._free.pop();
    if (this._created >= this._cap) return null;
    this._created++;
    return this._factory();
  }

  release(item) {
    if (!item) return;
    if (this._reset) this._reset(item);
    this._free.push(item);
  }

  releaseAll(items) {
    for (const i of items) this.release(i);
  }

  get size() { return this._created; }
  get free() { return this._free.length; }
  get inUse() { return this._created - this._free.length; }
}
